import { useState, useCallback } from 'react';
import { useToast } from './use-toast';
import { supabase } from '@/integrations/supabase/client';
import { debounce } from 'lodash';

interface MapboxFeature {
  id: string;
  place_name: string;
  text: string;
  center: [number, number]; // [longitude, latitude]
}

interface UseMapboxGeocodingProps {
  onSelect?: (place: MapboxFeature) => void;
}

// Debounced token fetching to prevent multiple simultaneous requests
const debouncedFetchToken = debounce(async () => {
  const { data, error } = await supabase.functions.invoke('get-mapbox-token');
  if (error) throw error;
  return data?.token;
}, 2000, { leading: true, trailing: false });

export const useMapboxGeocoding = ({ onSelect }: UseMapboxGeocodingProps) => {
  const [searchInput, setSearchInput] = useState('');
  const [predictions, setPredictions] = useState<MapboxFeature[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const { toast } = useToast();

  const fetchPredictions = useCallback(async (query: string) => {
    if (!query.trim()) {
      setPredictions([]);
      return;
    }

    try {
      // Get token from Edge Function
      const token = await debouncedFetchToken();
      
      if (!token) {
        console.error('No Mapbox token received from edge function');
        throw new Error('Configuration error');
      }

      // Restrict to US addresses and bias towards NYC
      const response = await fetch(
        `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(query)}.json?` + 
        `access_token=${token}&` +
        `country=us&` +
        `proximity=-74.006,40.7128&` + // NYC coordinates for better local results
        `types=address`
      );

      if (!response.ok) {
        throw new Error('Failed to fetch address suggestions');
      }

      const data = await response.json();
      setPredictions(data.features);
    } catch (error) {
      console.error('Error fetching predictions:', error);
      toast({
        title: "Error",
        description: "Failed to fetch address suggestions",
        variant: "destructive",
      });
      setPredictions([]);
    } finally {
      setIsLoading(false);
    }
  }, [toast]);

  // Debounce the API call with a shorter delay
  const handleInputChange = useCallback(debounce((e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchInput(value);
    fetchPredictions(value);
  }, 300), [fetchPredictions]); // Reduced from default 1000ms to 300ms for better responsiveness

  const handlePlaceSelect = (prediction: MapboxFeature) => {
    setSearchInput(prediction.place_name);
    setPredictions([]);
    onSelect?.(prediction);
  };

  return {
    searchInput,
    predictions,
    isLoading,
    handleInputChange,
    handlePlaceSelect,
  };
};